"use client";

import React, { FC, useState } from "react";
import { Button } from "@nextui-org/react";
import { ArrowLeftRight, Check, Copy, Trash2 } from "lucide-react";
import { copyToClipboard, saveText } from "@/lib/utils";
import { base64Decode, base64Encode, urlDecode, urlEncode } from "@/lib/textCodecs";
import { ToolName, trackToolAction } from "@/lib/analytics";

type CodecName = "base64" | "url";

const codecs: Record<CodecName, { encode: (input: string) => string; decode: (input: string) => string }> = {
    base64: { encode: base64Encode, decode: base64Decode },
    url: { encode: urlEncode, decode: urlDecode },
};

interface TextCodecProps {
    codec: CodecName;
    encodeLabel: string;
    decodeLabel: string;
    sampleInput: string;
    fileExtension: string;
}

type Mode = "encode" | "decode";

const TextCodec: FC<TextCodecProps> = ({ codec, encodeLabel, decodeLabel, sampleInput, fileExtension }) => {
    const toolName: ToolName = codec === "base64" ? "base64" : "url-encoder";
    const { encode: encodeFn, decode: decodeFn } = codecs[codec];
    const [mode, setMode] = useState<Mode>("encode");
    const [input, setInput] = useState<string>(sampleInput);
    const [output, setOutput] = useState<string>(() => {
        try {
            return sampleInput ? encodeFn(sampleInput) : "";
        } catch {
            return "";
        }
    });
    const [error, setError] = useState<string>("");
    const [copied, setCopied] = useState<boolean>(false);

    const runConversion = (value: string, activeMode: Mode) => {
        if (!value) {
            setOutput("");
            setError("");
            return;
        }
        try {
            const result = activeMode === "encode" ? encodeFn(value) : decodeFn(value);
            setOutput(result);
            setError("");
        } catch (e) {
            setOutput("");
            setError(`Unable to ${activeMode} this input. Please check it and try again.`);
        }
    };

    const handleInputChange = (value: string) => {
        trackToolAction(toolName, "edit");
        setInput(value);
        runConversion(value, mode);
    };

    const handleModeChange = (nextMode: Mode) => {
        trackToolAction(toolName, "mode_change", nextMode);
        setMode(nextMode);
        runConversion(input, nextMode);
    };

    const handleSwap = () => {
        trackToolAction(toolName, "mode_change", "swap");
        const nextMode: Mode = mode === "encode" ? "decode" : "encode";
        setMode(nextMode);
        setInput(output);
        runConversion(output, nextMode);
    };

    const handleCopy = async () => {
        if (output) {
            trackToolAction(toolName, "copy");
            await copyToClipboard(output);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="w-full flex flex-col items-center gap-4">
            <div className="flex gap-2">
                <Button
                    color={mode === "encode" ? "primary" : "default"}
                    variant={mode === "encode" ? "solid" : "flat"}
                    radius="sm"
                    onClick={() => handleModeChange("encode")}
                >
                    {encodeLabel}
                </Button>
                <Button
                    color={mode === "decode" ? "primary" : "default"}
                    variant={mode === "decode" ? "solid" : "flat"}
                    radius="sm"
                    onClick={() => handleModeChange("decode")}
                >
                    {decodeLabel}
                </Button>
            </div>

            <div className="w-full flex flex-col md:flex-row gap-4 items-stretch">
                <div className="w-full md:w-1/2 flex flex-col">
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Input</label>
                    <textarea
                        value={input}
                        onChange={(e) => handleInputChange(e.target.value)}
                        className="w-full h-[300px] p-4 font-mono text-sm border-2 border-gray-200 dark:border-gray-700 rounded-lg resize-none focus:outline-none focus:border-blue-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                        placeholder="Paste or type text here..."
                        spellCheck={false}
                    />
                </div>

                <div className="flex md:flex-col items-center justify-center">
                    <Button isIconOnly variant="light" radius="full" aria-label="Swap input and output" onClick={handleSwap}>
                        <ArrowLeftRight className="w-5 h-5 md:rotate-90" aria-hidden="true" />
                    </Button>
                </div>

                <div className="w-full md:w-1/2 flex flex-col">
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Output</label>
                    <textarea
                        value={output}
                        readOnly
                        className="w-full h-[300px] p-4 font-mono text-sm border-2 border-gray-200 dark:border-gray-700 rounded-lg resize-none focus:outline-none bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                        placeholder="Output will appear here..."
                        spellCheck={false}
                    />
                    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
            </div>

            <div className="flex flex-wrap gap-3">
                <Button
                    color="primary"
                    variant="bordered"
                    radius="sm"
                    onClick={handleCopy}
                    startContent={copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                >
                    {copied ? "Copied!" : "Copy Output"}
                </Button>
                <Button
                    color="primary"
                    variant="bordered"
                    radius="sm"
                    onClick={() => {
                        if (output) {
                            trackToolAction(toolName, "download");
                            saveText(output, fileExtension);
                        }
                    }}
                >
                    Download
                </Button>
                <Button
                    color="danger"
                    variant="bordered"
                    radius="sm"
                    startContent={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                    onClick={() => {
                        setInput("");
                        setOutput("");
                        setError("");
                    }}
                >
                    Clear
                </Button>
            </div>
        </div>
    );
};

export default TextCodec;
